import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import type { Database } from '@/types/database.types';

type Article = Database['public']['Tables']['articles']['Row'];
type ArticleUpdate = Database['public']['Tables']['articles']['Update'];

// Filtres pour la liste d'articles
export interface ArticlesFilters {
  status?: 'published' | 'draft' | 'archived' | 'all';
  category?: string;
  lang?: string;
  search?: string;
}

// Stats des articles
export interface ArticlesStats {
  total: number;
  published: number;
  drafts: number;
  archived: number;
  recentPublished: number;
  topViewed: Article[];
}

/**
 * Hook pour récupérer la liste des articles avec filtres (admin)
 * Les admins peuvent voir TOUS les articles, y compris les drafts
 */
export function useArticlesAdmin(filters?: ArticlesFilters) {
  return useQuery({
    queryKey: ['articles-admin', filters],
    queryFn: async () => {
      let query = supabase
        .from('articles')
        .select('*')
        .order('created_at', { ascending: false });

      // Filtrer par statut
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      // Filtrer par catégorie
      if (filters?.category) {
        query = query.eq('category', filters.category);
      }

      // Filtrer par langue
      if (filters?.lang) {
        query = query.eq('lang', filters.lang);
      }

      // Recherche par titre ou excerpt
      if (filters?.search) {
        query = query.or(
          `title.ilike.%${filters.search}%,excerpt.ilike.%${filters.search}%`
        );
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Article[];
    },
    staleTime: 30 * 1000, // 30 secondes
  });
}

/**
 * Hook pour mettre à jour un article (admin)
 */
export function useUpdateArticleAdmin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: ArticleUpdate }) => {
      const { data, error } = await supabase
        .from('articles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Article;
    },
    onSuccess: () => {
      // Invalider les requêtes articles pour forcer un refresh
      queryClient.invalidateQueries({ queryKey: ['articles-admin'] });
      queryClient.invalidateQueries({ queryKey: ['articles-stats'] });
    },
  });
}

/**
 * Hook pour supprimer un article (admin)
 */
export function useDeleteArticleAdmin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (articleId: string) => {
      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', articleId);

      if (error) throw error;
      return articleId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles-admin'] });
      queryClient.invalidateQueries({ queryKey: ['articles-stats'] });
    },
  });
}

/**
 * Hook pour récupérer un article par ID (admin)
 */
export function useArticleById(id: string | undefined) {
  return useQuery({
    queryKey: ['article-admin', id],
    queryFn: async () => {
      if (!id) throw new Error('ID article manquant');

      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as Article;
    },
    enabled: !!id,
  });
}

/**
 * Hook pour récupérer les statistiques des articles (admin)
 */
export function useArticlesStats() {
  return useQuery({
    queryKey: ['articles-stats'],
    queryFn: async () => {
      // Récupérer uniquement les colonnes nécessaires pour les stats
      const { data: allArticles, error: allError } = await supabase
        .from('articles')
        .select('id, status, published_at, view_count, title, slug');

      if (allError) throw allError;

      const articles = allArticles as Article[];

      // Calculer les stats
      const total = articles.length;
      const published = articles.filter((a) => a.status === 'published').length;
      const drafts = articles.filter((a) => a.status === 'draft').length;
      const archived = articles.filter((a) => a.status === 'archived').length;

      // Articles publiés dans les 30 derniers jours
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recentPublished = articles.filter(
        (a) =>
          a.status === 'published' &&
          a.published_at &&
          new Date(a.published_at) > thirtyDaysAgo
      ).length;

      // Top 5 articles les plus vus
      const topViewed = [...articles]
        .sort((a, b) => (b.view_count || 0) - (a.view_count || 0))
        .slice(0, 5);

      const stats: ArticlesStats = {
        total,
        published,
        drafts,
        archived,
        recentPublished,
        topViewed,
      };

      return stats;
    },
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook pour récupérer les catégories disponibles
 */
export function useArticleCategories() {
  return useQuery({
    queryKey: ['article-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('category')
        .not('category', 'is', null);

      if (error) throw error;

      // Extraire les catégories uniques
      const categories = Array.from(
        new Set(data.map((item) => item.category).filter(Boolean))
      ) as string[];

      return categories.sort();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
